import { useState, useEffect } from 'react'
import {
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Checkbox,
  Paper,
  CircularProgress,
} from '@mui/material'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { ConsignmentInfoPanel } from '../../../components/ConsignmentInfoPanel'
import SideModalWrapper from '../../Assets/modals/SideModalWrapper'
import { messages } from '../messages'
import { UnassignedConsignment, UnassignedLoad } from '../types'
import useJourneyStepperContext from '../hooks/useJourneyStepperContext'
import LoadContentsSidePanel from './LoadContentsSidePanel'
import { useGetUnassignedConsignmentLoadsQuery } from '../../../app/redux-fetch/apiOrders'
import { addLoadMetadata } from '../Planner/state/journeyPlannerSlice'

type ConsignmentLoadsSidePanelProps = {
  open: boolean
  onClose: () => void
  consignment: UnassignedConsignment | null
  tenantId: number
}

const ConsignmentLoadsSidePanel = ({
  open,
  onClose,
  consignment,
  tenantId,
}: ConsignmentLoadsSidePanelProps) => {
  const { formatMessage } = useIntl()
  const reduxDispatch = useDispatch()
  const { selectedLoads, setSelectedLoads } = useJourneyStepperContext()
  const [selectedLoad, setSelectedLoad] = useState<UnassignedLoad | null>(null)
  const [contentsOpen, setContentsOpen] = useState(false)

  const { data: loads, isLoading, isError } = useGetUnassignedConsignmentLoadsQuery(
    { tenantId, shipmentBookingId: consignment?.shipmentBookingId ?? 0 },
    { skip: !open || !consignment }
  )

  // Store load metadata in Redux when loads are fetched
  useEffect(() => {
    if (loads && loads.length > 0) {
      reduxDispatch(addLoadMetadata(loads))
    }
  }, [loads, reduxDispatch])

  const handleLoadClick = (load: UnassignedLoad) => {
    setSelectedLoad(load)
    setContentsOpen(true)
  }

  const handleContentsClose = () => {
    setContentsOpen(false)
    setSelectedLoad(null)
  }

  if (!consignment) return null

  const handleLoadSelectionChange = (loadId: number, checked: boolean) => {
    if (checked) {
      setSelectedLoads([...selectedLoads, { loadId, shipmentBookingId: consignment.shipmentBookingId }])
    } else {
      setSelectedLoads(selectedLoads.filter((sl) => sl.loadId !== loadId))
    }
  }

  const isLoadSelected = (loadId: number) => selectedLoads.some((sl) => sl.loadId === loadId)

  const renderMultilineCell = (value: string | undefined) => {
    if (!value) return formatMessage(messages.notAvailable)
    return value.split('\n').map((line, index) => (
      <div key={index}>{line || formatMessage(messages.notAvailable)}</div>
    ))
  }

  return (
    <SideModalWrapper
      open={open}
      onClose={onClose}
      headerLabel={formatMessage(messages.selectLoads)}
      withBottomBox={false}
      width="62.5%"
    >
      <Box sx={{ p: '32px' }}>
        <ConsignmentInfoPanel
          data={{
            shipperName: consignment.shipperName,
            loadTypeName: consignment.loadTypeName,
            description: consignment.description,
            currencyCode: consignment.currencyCode,
            trackingNumber: consignment.trackingNumber,
            customerReferenceNumber: consignment.referenceNumber,
          }}
          variant="sidePanel"
        />

        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          {formatMessage(messages.loads)}
        </Typography>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{formatMessage(messages.loads)}</TableCell>
                <TableCell>{formatMessage(messages.numberOfLoads)}</TableCell>
                <TableCell>{formatMessage(messages.serviceWaypoint)}</TableCell>
                <TableCell>{formatMessage(messages.service)}</TableCell>
                <TableCell>{formatMessage(messages.loadType)}</TableCell>
                <TableCell>{formatMessage(messages.tempRange)}</TableCell>
                <TableCell padding="checkbox"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {isError && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    {formatMessage(messages.errorLoadingData)}
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && !isError && loads?.map((load: UnassignedLoad) => (
                <TableRow key={load.loadId}>
                  <TableCell>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => handleLoadClick(load)}
                      sx={{ cursor: 'pointer' }}
                    >
                      {load.trackingNumber ?? formatMessage(messages.notAvailable)}
                    </Link>
                  </TableCell>
                  <TableCell>{load.quantity}</TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-line' }}>
                    {renderMultilineCell(load.serviceWaypointLocations)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-line' }}>
                    {renderMultilineCell(load.services)}
                  </TableCell>
                  <TableCell>
                    {load.loadTypeName ?? formatMessage(messages.notAvailable)}
                  </TableCell>
                  <TableCell>
                    {load.temperatureRange ?? formatMessage(messages.notAvailable)}
                  </TableCell>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isLoadSelected(load.loadId)}
                      onChange={(e) => handleLoadSelectionChange(load.loadId, e.target.checked)}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {!isLoading && !isError && (!loads || loads.length === 0) && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    {formatMessage(messages.noLoads)}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <LoadContentsSidePanel
        open={contentsOpen}
        onClose={handleContentsClose}
        consignment={consignment}
        load={selectedLoad}
        tenantId={tenantId}
      />
    </SideModalWrapper>
  )
}

export default ConsignmentLoadsSidePanel
